import { ArrowCounterClockwise } from '@phosphor-icons/react';
import type { DraftDto, FormIssue } from '@app/shared';
import { useT } from '../i18n';
import type { AutosaveStatus } from '../lib/autosave';
import { cn } from '../lib/cn';
import { blockLabel, flagsByField, lastEditFailed, type EditorAction, type EditorState } from '../lib/draft-editor';
import { formatLimitSeconds, formatSeconds } from '../lib/format';
import { issueText } from '../lib/form-issues';
import { DraftBlockEditor } from './DraftBlock';
import { HelpHint } from './HelpHint';
import { Button, Card, Spinner, Toast } from './ui';

export interface TextBlocksEditorProps {
  draft: DraftDto;
  editor: EditorState;
  /** Form issues of the text on the screen (live). */
  issues: FormIssue[];
  /** Reel: the estimated seconds of the text on the screen. */
  estimate: number | null;
  /** An AI edit runs or an action waits: the fields are read-only. */
  readOnly: boolean;
  acting: string | null;
  saveStatus: AutosaveStatus;
  /** A build refused at this block of the saved version (?block=b2). */
  focusBlock: string | null;
  onEdit: (action: EditorAction) => void;
  onBlur: () => void;
  onFlag: (flagId: string, action: 'keep' | 'remove') => void;
  onSuggestion: (suggestionId: string, action: 'add' | 'dismiss') => void;
  onUndo: () => void;
}

/**
 * The text of «Формат и текст» (spec 4.4): «Текст» and the reel's length, the
 * blocks always editable with their notes under them, the suggestions after the
 * text, the form issues, «Отменить правку» and the save status. Edits save by
 * themselves; the page owns the saving.
 */
export function TextBlocksEditor({
  draft,
  editor,
  issues,
  estimate,
  readOnly,
  acting,
  saveStatus,
  focusBlock,
  onEdit,
  onBlur,
  onFlag,
  onSuggestion,
  onUndo,
}: TextBlocksEditorProps) {
  const t = useT();
  const f = t.formatText;
  const { format, limits } = draft;
  const total = editor.blocks.length;
  const openFlags = draft.flags.filter((x) => x.state === 'open');
  const flagsAt = flagsByField(openFlags, editor, draft.blocks);
  const openSuggestions = draft.suggestions.filter((s) => s.state === 'open');
  const focusIndex = focusBlock ? draft.blocks.findIndex((b) => b.id === focusBlock) : -1;
  const position = (blockId: string) => Number(blockId.slice(1)) || 0;
  const status =
    saveStatus === 'pending' || saveStatus === 'saving'
      ? f.saving
      : saveStatus === 'saved'
        ? f.saved
        : saveStatus === 'failed'
          ? f.saveFailed
          : saveStatus === 'conflict'
            ? f.conflict
            : null;

  return (
    <section>
      <div className="flex flex-wrap items-baseline gap-2 mb-2">
        <h2 className="flex items-center gap-1.5 text-base font-bold">
          {f.text}
          <HelpHint topic="text" />
        </h2>
        {estimate !== null && (
          <span
            className={cn(
              'text-xs tabular-nums',
              estimate > limits.reel.maxDurationS ? 'text-destructive font-semibold' : 'text-hint',
            )}
          >
            {f.estimate(formatSeconds(estimate), formatLimitSeconds(limits.reel.maxDurationS))}
          </span>
        )}
      </div>

      {draft.checkStatus === 'failed' && (
        <Toast tone="warning" className="mb-3">
          {t.draft.checkFailed}
        </Toast>
      )}
      {lastEditFailed(draft) && (
        <Toast tone="warning" className="mb-3">
          {t.draft.editFailed}
        </Toast>
      )}
      {openFlags.length > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-hint mb-2">
          {t.draft.flagsTitle}
          <HelpHint topic="flags" />
        </p>
      )}
      {draft.status === 'editing' && (
        <p className="flex items-center gap-2 text-sm text-hint mb-2">
          <Spinner size="sm" />
          {f.aiEditing}
        </p>
      )}

      <div className="space-y-3 mb-3">
        {editor.blocks.map((b, i) => (
          <DraftBlockEditor
            key={b.key}
            block={b}
            index={i}
            total={total}
            label={blockLabel(format, i, total, t)}
            format={format}
            limits={limits}
            readOnly={readOnly}
            flags={flagsAt.get(b.key) ?? []}
            acting={acting}
            highlighted={i === focusIndex}
            onText={(value) => onEdit({ type: 'text', key: b.key, value })}
            onTitle={(value) => onEdit({ type: 'title', key: b.key, value })}
            onBlur={onBlur}
            onRemove={() => onEdit({ type: 'remove', key: b.key })}
            onInsertAfter={() => onEdit({ type: 'insertAfter', key: b.key })}
            onFlag={onFlag}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-4 min-h-8">
        <span className={cn('text-xs', saveStatus === 'failed' || saveStatus === 'conflict' ? 'text-warning' : 'text-hint')}>
          {status}
        </span>
        {draft.canUndo && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowCounterClockwise size={14} />}
            disabled={readOnly || acting !== null}
            loading={acting === 'undo'}
            onClick={onUndo}
          >
            {f.undo}
          </Button>
        )}
      </div>

      {draft.type === 'listicle' && (
        <p className="text-xs text-hint mb-4 break-words">
          {t.draft.codewordHint} <HelpHint topic="codeword" />
        </p>
      )}

      {openSuggestions.length > 0 && (
        <section className="mb-4">
          <h2 className="text-sm font-semibold mb-2">{t.draft.suggestionsTitle}</h2>
          <div className="space-y-2">
            {openSuggestions.map((s) => (
              <Card key={s.id} variant="surface" padding="md">
                <p className="text-sm mb-2 break-words">{s.text}</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={readOnly || acting !== null}
                    loading={acting === `add:${s.id}`}
                    onClick={() => onSuggestion(s.id, 'add')}
                  >
                    {t.draft.add}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={readOnly || acting !== null}
                    loading={acting === `dismiss:${s.id}`}
                    onClick={() => onSuggestion(s.id, 'dismiss')}
                  >
                    {t.draft.dismiss}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {issues.length > 0 && (
        <Card variant="outline" padding="md" className="mb-4 border-destructive/40">
          <h2 className="text-sm font-semibold mb-2 text-destructive">{f.issuesTitle}</h2>
          <ul className="space-y-1 text-sm">
            {issues.map((issue, i) => (
              <li key={i} className="break-words">
                {issueText(issue, t, position)}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}
